/**
 * AECampaigns — AE home dashboard.
 * Lists every brand campaign on the platform with assignment + progress + filters.
 * Filters: All | Assigned to me | Unassigned | Overdue
 * Click a card → /ae/campaigns/{id}/oversight
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Briefcase, UserCog, Users, AlertTriangle, Clock, ArrowRight, ShieldCheck, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { AssignCMTMDialog } from '../../components/ae/AssignCMTMDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'assigned',    label: 'Assigned to me' },
  { key: 'unassigned',  label: 'Unassigned' },
  { key: 'overdue',     label: 'Overdue' },
];

export const AECampaigns = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [assignDialog, setAssignDialog] = useState(null); // {campaign, kind}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/ae/campaigns`, { withCredentials: true });
      setCampaigns(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: campaigns.length, assigned: 0, unassigned: 0, overdue: 0 };
    for (const x of campaigns) {
      if (x.assigned_ae_id === user?.id) c.assigned++;
      if (!x.assigned_ae_id) c.unassigned++;
      if (x.is_overdue) c.overdue++;
    }
    return c;
  }, [campaigns, user]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'assigned':   return campaigns.filter((c) => c.assigned_ae_id === user?.id);
      case 'unassigned': return campaigns.filter((c) => !c.assigned_ae_id);
      case 'overdue':    return campaigns.filter((c) => c.is_overdue);
      default:           return campaigns;
    }
  }, [campaigns, filter, user]);

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-8" data-testid="ae-campaigns-header">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={14} className="text-[#FF451A]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500">Account Executive · Oversight</span>
              </div>
              <h1 className="font-display text-4xl text-[#0F0F0F] dark:text-white tracking-tight">All Campaigns</h1>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 mt-2">Every campaign on the platform, with CM/TM assignment and live progress.</p>
            </div>
            <Button variant="ghost" onClick={load} disabled={loading} className="text-zinc-300 hover:text-white" data-testid="ae-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-1.5 mb-6" data-testid="ae-filter-bar">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${active ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
                  data-testid={`ae-filter-${f.key}`}
                >
                  {f.label} <span className="opacity-60">· {counts[f.key]}</span>
                </button>
              );
            })}
          </div>

          {/* List */}
          {loading ? (
            <p className="text-[#6B6B6B] dark:text-zinc-500 text-sm py-8 text-center" data-testid="ae-loading">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-12 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="ae-empty">
              No campaigns match this filter.
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => (
                <div key={c.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid="ae-campaign-card">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">{c.brand_name}</span>
                        {c.campaign_type === 'bulk' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[#FF451A]/15 text-[#FF451A]">BULK</span>
                        )}
                        {c.is_overdue && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[#FF3B30]/15 text-[#FF3B30] inline-flex items-center gap-1">
                            <AlertTriangle size={9} /> OVERDUE
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate">{c.campaign_name}</h3>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-[#6B6B6B] dark:text-zinc-400 font-mono flex-wrap">
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {c.creator_count} creators</span>
                        <span className="text-[#A1A1AA] dark:text-zinc-600">·</span>
                        <span>CM: {c.campaign_managers.length ? c.campaign_managers.map((m) => m.name).join(', ') : <span className="text-[#A1A1AA] dark:text-zinc-600 italic">unassigned</span>}</span>
                        <span className="text-[#A1A1AA] dark:text-zinc-600">·</span>
                        <span>TM: {c.talent_managers.length ? c.talent_managers.map((m) => m.name).join(', ') : <span className="text-[#A1A1AA] dark:text-zinc-600 italic">unassigned</span>}</span>
                        <span className="text-[#A1A1AA] dark:text-zinc-600">·</span>
                        <span className="inline-flex items-center gap-1"><Clock size={11} /> {c.days_since_update ?? 0}d ago</span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
                        <span>Live · {c.progress?.live ?? 0}</span>
                        <span>Completed · {c.progress?.completed ?? 0}</span>
                        <span>Addr · {c.progress?.addresses_collected ?? 0}/{c.progress?.total ?? 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        onClick={() => setAssignDialog({ campaign: c, kind: 'cm' })}
                        className="bg-white/5 hover:bg-white/10 text-[#0F0F0F] dark:text-white text-xs px-3 py-1.5 rounded-full border border-black/[0.10] dark:border-white/10"
                        data-testid={`ae-assign-cm-${c.id}`}
                      >
                        <Briefcase size={12} className="mr-1" /> Assign CM
                      </Button>
                      <Button
                        onClick={() => setAssignDialog({ campaign: c, kind: 'tm' })}
                        className="bg-white/5 hover:bg-white/10 text-[#0F0F0F] dark:text-white text-xs px-3 py-1.5 rounded-full border border-black/[0.10] dark:border-white/10"
                        data-testid={`ae-assign-tm-${c.id}`}
                      >
                        <UserCog size={12} className="mr-1" /> Assign TM
                      </Button>
                      <Link
                        to={`/ae/campaigns/${c.id}`}
                        className="inline-flex items-center gap-1 bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white text-xs px-3 py-1.5 rounded-full"
                        data-testid={`ae-open-${c.id}`}
                      >
                        Oversight <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {assignDialog && (
        <AssignCMTMDialog
          campaign={assignDialog.campaign}
          kind={assignDialog.kind}
          onClose={() => setAssignDialog(null)}
          onAssigned={load}
        />
      )}
    </div>
  );
};
